import React, { useState, useEffect } from "react";
import { ref, get } from "firebase/database";
import { db } from "./firebase/config";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { createSlug } from "./utils/slugUtils";

const ARTICLES_PER_PAGE = 12;

const NewsScreen = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsRef = ref(db, "news");
        const snapshot = await get(newsRef);

        if (snapshot.exists()) {
          const newsData = snapshot.val();
          const newsArray = Object.entries(newsData).map(([id, data]) => {
            return {
              id,
              ...data,
              createdAt: data.createdAt
                ? new Date(data.createdAt).getTime()
                : 0,
              slug: createSlug(data.title),
            };
          });

          const sortedArticles = newsArray.sort(
            (a, b) => b.createdAt - a.createdAt
          );

          setArticles(sortedArticles);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Pagination
  const totalPages = Math.ceil(articles.length / ARTICLES_PER_PAGE);
  const validPage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const startIndex = (validPage - 1) * ARTICLES_PER_PAGE;
  const paginatedArticles = articles.slice(
    startIndex,
    startIndex + ARTICLES_PER_PAGE
  );

  const goToPage = (page) => {
    if (page === 1) {
      setSearchParams({});
    } else {
      setSearchParams({ page: page.toString() });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const pageTitle =
    validPage > 1
      ? `Tin Tức & Cập Nhật - Trang ${validPage} | AutoWork`
      : "Tin Tức & Cập Nhật | AutoWork";

  const canonicalUrl =
    validPage > 1
      ? `https://autowork.com.vn/news?page=${validPage}`
      : "https://autowork.com.vn/news";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="animate-pulse">
          <div className="h-64 bg-gradient-to-r from-blue-500 to-purple-600"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Cập nhật tin tức mới nhất về trí tuệ nhân tạo (AI), công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả."
        />
        <meta
          name="keywords"
          content="tin tức AI, công nghệ, tự động hóa marketing, quản lý fanpage, content marketing, autowork"
        />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content="Cập nhật tin tức mới nhất về trí tuệ nhân tạo (AI), công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="AutoWork" />
        <meta property="og:locale" content="vi_VN" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta
          name="twitter:description"
          content="Cập nhật tin tức mới nhất về trí tuệ nhân tạo (AI), công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả."
        />
        <meta name="twitter:image" content="https://autowork.com.vn/logo1.png" />
        <meta property="og:image" content="https://autowork.com.vn/logo1.png" />

        {/* Pagination rel links */}
        {validPage > 1 && (
          <link
            rel="prev"
            href={
              validPage === 2
                ? "https://autowork.com.vn/news"
                : `https://autowork.com.vn/news?page=${validPage - 1}`
            }
          />
        )}
        {validPage < totalPages && (
          <link
            rel="next"
            href={`https://autowork.com.vn/news?page=${validPage + 1}`}
          />
        )}

        {/* Blog Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Tin Tức & Cập Nhật | AutoWork",
            description:
              "Cập nhật tin tức mới nhất về trí tuệ nhân tạo (AI), công nghệ tự động hóa marketing và các giải pháp hiệu quả.",
            url: "https://autowork.com.vn/news",
            publisher: {
              "@type": "Organization",
              name: "AutoWork",
              logo: {
                "@type": "ImageObject",
                url: "https://autowork.com.vn/logo1.png",
              },
            },
            inLanguage: "vi",
            blogPost: paginatedArticles.map((article) => ({
              "@type": "BlogPosting",
              headline: article.title,
              description: article.description,
              image: article.imageUrl,
              datePublished: new Date(article.createdAt).toISOString(),
              dateModified: article.updatedAt
                ? new Date(article.updatedAt).toISOString()
                : new Date(article.createdAt).toISOString(),
              author: {
                "@type": "Organization",
                name: "AutoWork",
              },
              url: `https://autowork.com.vn/news/${article.slug}`,
            })),
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Trang chủ",
                item: "https://autowork.com.vn",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Tin tức",
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-0 md:pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-gray-500">
              <li>
                <Link to="/" className="hover:text-blue-600 transition-colors">
                  Trang chủ
                </Link>
              </li>
              <li>
                <span className="mx-1">/</span>
              </li>
              <li className="text-gray-900 font-medium">Tin tức</li>
            </ol>
          </nav>
        </div>

        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white mt-4">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">
                Tin Tức & Cập Nhật
              </h1>
              <p className="text-lg text-gray-100 max-w-2xl mx-auto">
                Cập nhật những tin tức mới nhất về trí tuệ nhân tạo (AI), công
                nghệ hiện đại và các giải pháp tự động hóa đột phá
              </p>
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paginatedArticles.map((article) => (
              <article
                key={article.id}
                className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="relative overflow-hidden">
                  {article.imageUrl ? (
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      width={400}
                      height={192}
                      className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-3 flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <time
                      dateTime={new Date(article.createdAt).toISOString()}
                    >
                      {formatDate(article.createdAt)}
                    </time>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    <Link to={`/news/${article.slug}`}>{article.title}</Link>
                  </h2>
                  <p className="text-gray-600 line-clamp-3 mb-4">
                    {article.description}
                  </p>
                  <Link
                    to={`/news/${article.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Đọc thêm
                    <svg
                      className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              className="flex justify-center items-center mt-12 mb-8"
              aria-label="Phân trang"
            >
              <div className="flex items-center space-x-2">
                {/* Previous Button */}
                <button
                  onClick={() => goToPage(validPage - 1)}
                  disabled={validPage <= 1}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    validPage <= 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-300 shadow-sm"
                  }`}
                  aria-label="Trang trước"
                >
                  ← Trước
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first, last, current, and adjacent pages
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - validPage) <= 1
                    );
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {/* Ellipsis */}
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => goToPage(page)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                          page === validPage
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-300"
                        }`}
                        aria-label={`Trang ${page}`}
                        aria-current={
                          page === validPage ? "page" : undefined
                        }
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}

                {/* Next Button */}
                <button
                  onClick={() => goToPage(validPage + 1)}
                  disabled={validPage >= totalPages}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    validPage >= totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-300 shadow-sm"
                  }`}
                  aria-label="Trang sau"
                >
                  Sau →
                </button>
              </div>
            </nav>
          )}

          {/* Page Info */}
          {totalPages > 1 && (
            <p className="text-center text-sm text-gray-500 mb-8">
              Trang {validPage} / {totalPages} • Tổng {articles.length} bài
              viết
            </p>
          )}

          {articles.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                Chưa có bài viết nào
              </h3>
              <p className="text-gray-500">
                Vui lòng quay lại sau để xem các bài viết mới.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsScreen;
